import { fetchServices, NB_SERVICES_PER_PAGE } from 'features/services/business/Services.api';
import { AllServiceRatingCode } from 'features/services/business/Services.enum';
import { ServicesHomeLayout } from 'features/services/components/ServicesHomeLayout/ServicesHomeLayout.component';
import { ActivityCode, Content, InlineResponse2001, ProjectCode } from 'pleinchamp-api-client';
import React, { createContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePlcApiCall } from '@api/business/api.hook';
import { AppDispatch } from '@store/store';
import { ExtendedPagination, extendPagination } from '@utils/pagination';
import { defaultNamespaces } from '@utils/strings';
import { PlcNextPage, PlcNextPageContext, PlcPageContextProps } from 'features/business/app.types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

interface PageProps {
  servicesContent: InlineResponse2001;
  activities: ActivityCode[];
  projects: ProjectCode[];
  rating: AllServiceRatingCode;
}
const NAMESPACES = [...defaultNamespaces, 'services'];
interface ContentProps {
  services: Content[];
  pagination: ExtendedPagination;
}

type ContextProps = PlcPageContextProps<Omit<PageProps, 'servicesContent'> & ContentProps> & {
  fetchNextServices?: () => void;
};

export const ServicesHomeContext = createContext<ContextProps>({
  activities: { isValidating: false },
  pagination: { isValidating: false },
  projects: { isValidating: false },
  rating: { isValidating: false },
  services: { isValidating: false },
});

const ServicesHome: PlcNextPage<Partial<PageProps>> = (props) => {
  const [activities, setActivities] = useState<ActivityCode[]>([]);
  const [projects, setProjects] = useState<ProjectCode[]>([]);
  const [rating, setRating] = useState<AllServiceRatingCode | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [services, setServices] = useState(props.servicesContent ? props.servicesContent.contents : []);
  const [pagination, setPagination] = useState(
    props.servicesContent ? extendPagination(props.servicesContent.pagination) : undefined
  );

  const { data, initialData, ...restServicesContent } = usePlcApiCall(
    fetchServices,
    [activities, projects, rating, offset],
    {
      initialData: props.servicesContent,
    }
  );

  useEffect(() => {
    if (data) {
      const { pagination: nextPagination, contents } = data;
      setServices(services.slice(0, nextPagination.offset).concat(contents));
      setPagination(extendPagination(nextPagination));
    }
  }, [data]);

  function fetchNextServices() {
    if (pagination?.hasNext) {
      setOffset(offset + NB_SERVICES_PER_PAGE);
    }
  }

  const {
    services: { fetchUserServices },
  } = useDispatch<AppDispatch>();
  useEffect(() => {
    fetchUserServices();
  }, []);

  return (
    <ServicesHomeContext.Provider
      value={{
        activities: { data: activities, isValidating: false, setData: setActivities },
        fetchNextServices,
        pagination: { ...restServicesContent, data: pagination },
        projects: { data: projects, isValidating: false, setData: setProjects },
        rating: { data: rating, isValidating: false, setData: setRating },
        services: { ...restServicesContent, data: services },
      }}
    >
      <ServicesHomeLayout />
    </ServicesHomeContext.Provider>
  );
};

export async function getServerSideProps({ locale }: PlcNextPageContext) {
  return Promise.all([fetchServices(), serverSideTranslations(locale || 'fr', NAMESPACES)])
    .then(([servicesContent, i18n]) => {
      return {
        props: { servicesContent, ...i18n },
      };
    })
    .catch(async (err) => {
      console.error('Les-outils getServerSideProps error', err);
      return {
        props: { ...(await serverSideTranslations(locale || 'fr', NAMESPACES)) },
      };
    });
}

export default ServicesHome;
